import { useEffect, useMemo, useSyncExternalStore } from "react";

import type { CmmdForgeRuntimeApiConfig } from "../environments/primary/cmmdForgeRuntimeApi";
import { setCloudAuthFailure } from "../rpc/wsConnectionState";
import { useStore } from "../store";
import { resolveCheckoutRefsForRows } from "./cmmdCheckoutResolution";
import {
  cloudEnvironmentRowsFromApiRecords,
  cloudProjectRowsFromApiRecords,
  cloudProjectRuntimeFields,
} from "./cmmdCloudProjectRowMapping";
import { loadCachedRows, persistRows } from "./cmmdForgeRuntimeListCache";
import {
  CMMD_CLOUD_CATALOG_REQUEST_TIMEOUT_MS,
  CLOUD_PROJECT_CATALOG_EVENTS_URL,
  CLOUD_PROJECT_CATALOG_LIST_URL,
  createCmmdCloudCatalogTransport,
  type CmmdCloudCatalogTransport,
  type CmmdCloudCatalogTransportEvent,
} from "./cmmdForgeRuntimeListTransport";
import type { CmmdForgeRuntimeListSnapshot } from "./cmmdForgeRuntimeListTypes";

export type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
  CmmdForgeRuntimeListSnapshot,
} from "./cmmdForgeRuntimeListTypes";

export const __cloudProjectRuntimeFieldsForTests = cloudProjectRuntimeFields;
export { CMMD_CLOUD_CATALOG_REQUEST_TIMEOUT_MS };

type Listener = () => void;

interface ResourceDeps {
  readonly config: CmmdForgeRuntimeApiConfig;
}

const INITIAL_SNAPSHOT: CmmdForgeRuntimeListSnapshot = {
  connected: false,
  isLoading: false,
  error: null,
  rows: [],
  environments: [],
  fetchedAt: null,
  stale: false,
  startupPhase: "idle",
  capacity: null,
};

export interface CmmdForgeRuntimeListResource {
  readonly subscribe: (listener: Listener) => () => void;
  readonly read: () => CmmdForgeRuntimeListSnapshot;
  readonly initialize: () => void;
  readonly refresh: () => void;
  readonly _teardown: () => void;
  readonly _setState: (next: CmmdForgeRuntimeListSnapshot) => void;
}

export function createCmmdForgeRuntimeListResource({
  config,
}: ResourceDeps): CmmdForgeRuntimeListResource {
  const cached = loadCachedRows();
  let snapshot: CmmdForgeRuntimeListSnapshot = cached
    ? {
        ...INITIAL_SNAPSHOT,
        isLoading: true,
        rows: cached.rows,
        environments: cached.environments,
        fetchedAt: cached.fetchedAt,
        stale: true,
      }
    : INITIAL_SNAPSHOT;
  const listeners = new Set<Listener>();

  function emit(next: CmmdForgeRuntimeListSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  function reduceTransportEvent(event: CmmdCloudCatalogTransportEvent): void {
    switch (event.kind) {
      case "loading":
        emit({ ...snapshot, isLoading: true, error: null });
        return;
      case "catalog": {
        const rows = cloudProjectRowsFromApiRecords(event.catalog.listRowsForDisplay());
        const environments = cloudEnvironmentRowsFromApiRecords(
          event.catalog.listEnvironmentsForDisplay(),
        );
        persistRows(rows, environments, event.fetchedAt);
        emit({
          connected: true,
          isLoading: false,
          error: null,
          rows,
          environments,
          fetchedAt: event.fetchedAt,
          stale: false,
          startupPhase: "idle",
          capacity: event.catalog.capacity,
        });
        return;
      }
      case "failed":
        emit({
          ...snapshot,
          connected: false,
          isLoading: false,
          error: event.message,
          stale: snapshot.rows.length > 0,
          startupPhase: "idle",
        });
        return;
      case "auth-refreshing":
        emit({
          ...snapshot,
          connected: false,
          isLoading: true,
          error: null,
          stale: snapshot.rows.length > 0,
          startupPhase: "auth-refreshing",
        });
        return;
      case "auth-required":
        emit({
          ...snapshot,
          connected: false,
          isLoading: false,
          error: event.message,
          stale: snapshot.rows.length > 0,
          startupPhase: "idle",
        });
        setCloudAuthFailure("cmmd-runtimes");
        return;
    }
  }

  const transport: CmmdCloudCatalogTransport = createCmmdCloudCatalogTransport({
    config,
    onEvent: reduceTransportEvent,
  });

  return {
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(): CmmdForgeRuntimeListSnapshot {
      return snapshot;
    },
    initialize(): void {
      transport.initialize();
    },
    refresh(): void {
      transport.refresh();
    },
    _teardown(): void {
      transport.teardown();
    },
    _setState(next: CmmdForgeRuntimeListSnapshot): void {
      emit(next);
    },
  };
}

let singleton: CmmdForgeRuntimeListResource | null = null;

function resolveSameOriginProxyUrl(path: string): string {
  if (typeof window !== "undefined" && window.location?.href) {
    return new URL(path, window.location.href).toString();
  }
  return new URL(path, "http://localhost").toString();
}

export function resolveSingletonConfig(): CmmdForgeRuntimeApiConfig {
  const cmmdHttpBaseUrl =
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_COMMAND_APP_URL?.trim()) ||
    "https://cmmd.ai";
  return {
    cmmdHttpBaseUrl,
    resolveAccessToken: () => null,
    listUrl: CLOUD_PROJECT_CATALOG_LIST_URL,
    eventsUrl: CLOUD_PROJECT_CATALOG_EVENTS_URL,
    destroyUrl: (runtimeId) =>
      resolveSameOriginProxyUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/destroy`,
      ),
    lifecycleUrl: (runtimeId, action) =>
      resolveSameOriginProxyUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/${action}`,
      ),
    updateCommitUrl: (runtimeId) =>
      resolveSameOriginProxyUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/update-commit`,
      ),
    updateCommitOperationUrl: (runtimeId, operationId) =>
      resolveSameOriginProxyUrl(
        `/api/cmmd/forge-runtimes/${encodeURIComponent(runtimeId)}/update-commit/${encodeURIComponent(String(operationId))}`,
      ),
    ensureUrl: resolveSameOriginProxyUrl("/api/cmmd/forge-runtimes/ensure"),
  };
}

function getSingleton(): CmmdForgeRuntimeListResource {
  if (singleton === null) {
    singleton = createCmmdForgeRuntimeListResource({ config: resolveSingletonConfig() });
  }
  return singleton;
}

export const cmmdForgeRuntimeListResource = {
  refresh(): void {
    getSingleton().refresh();
  },
  _reset(): void {
    singleton?._teardown();
    singleton = null;
  },
};

export function refreshCmmdForgeRuntimeList(): void {
  getSingleton().refresh();
}

function subscribeCmmdForgeRuntimeList(listener: Listener): () => void {
  return getSingleton().subscribe(listener);
}

export function readCmmdForgeRuntimeListSnapshot(): CmmdForgeRuntimeListSnapshot {
  return getSingleton().read();
}

/**
 * The one place Checkout resolution runs for every reader of the runtime
 * list: `readCmmdForgeRuntimeListSnapshot` (the resource) has no access to
 * the zustand store's Checkout residents, so `resolvedRef` on its rows is
 * always null. This hook is where the snapshot and the store first meet, so
 * resolution runs here, exactly once per snapshot/store change, and every
 * caller downstream (sidebar, `/new` composer) reads the already-resolved
 * row rather than re-deriving it. The hook also owns the resource's passive,
 * idempotent startup so direct routes do not depend on sidebar or Settings
 * components being mounted.
 */
export function useCmmdForgeRuntimeListSnapshot(options?: {
  readonly enabled?: boolean;
}): CmmdForgeRuntimeListSnapshot {
  const enabled = options?.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    getSingleton().initialize();
  }, [enabled]);
  const snapshot = useSyncExternalStore(
    subscribeCmmdForgeRuntimeList,
    readCmmdForgeRuntimeListSnapshot,
    readCmmdForgeRuntimeListSnapshot,
  );
  const environmentStateById = useStore((state) => state.environmentStateById);
  const rows = useMemo(
    () => resolveCheckoutRefsForRows(snapshot.rows, environmentStateById),
    [snapshot.rows, environmentStateById],
  );
  return rows === snapshot.rows ? snapshot : { ...snapshot, rows };
}
